// Browser-native voice mode for Practice — STT via SpeechRecognition,
// TTS via speechSynthesis. Borrows from IliaLarchenko/Interviewer the
// "paragraph-streaming overlap" trick (ui/coding.py:87-104):
// instead of waiting for the full LLM response (~30-60s), we slice
// completed sentences off the streaming token buffer and queue them for
// TTS playback as soon as a sentence boundary appears, so the user hears
// audio within 1-2s of the first token.
//
// Browser support reality:
//   - Chrome/Edge: webkitSpeechRecognition full support
//   - Firefox: NOT supported (returns null caps); fall back to text mode
//   - Safari: spotty support
// `caps.canRecognize` lets the UI hide the mic toggle when STT is missing.
//
// Localhost / HTTPS only — webkitSpeechRecognition refuses other origins.

import { useEffect, useRef, useState, useCallback } from 'react';

// Augment global typing without polluting other modules
type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string };
  }>;
};

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
  onspeechstart: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

interface SpeechRecognitionCtor {
  new (): SpeechRecognitionLike;
}

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export interface VoiceLoopCaps {
  canRecognize: boolean;  // STT available
  canSpeak: boolean;      // TTS available
}

export interface UseVoiceLoopOptions {
  enabled: boolean;
  lang?: string;                              // default "en-US"
  onFinalUtterance?: (text: string) => void;  // user finished speaking a chunk
  onBargeIn?: () => void;                     // user starts speaking while bot speaks
}

export interface VoiceLoopApi {
  caps: VoiceLoopCaps;
  listening: boolean;
  speaking: boolean;
  interim: string;             // live partial transcript
  error: string | null;
  lang: string;                // current STT language (e.g. "en-US", "zh-CN")
  startListening: () => void;
  stopListening: () => void;
  /** Manual rescue: commit the last heard interim as a final transcript.
   *  Returns true if there was something to commit. */
  useLastHeard: () => boolean;
  speakStreamReset: () => void;
  speakStreamPush: (chunk: string) => void;
  stopSpeaking: () => void;
}

// Sentence boundary detector. Bias toward speaking earlier rather than
// hoarding text — qwen3 generates slowly, so as soon as we have a
// meaningful sentence we ship it to TTS. Min length 30 chars guards
// against splitting on "U.S." or "e.g." style false positives.
const MIN_SENTENCE_LEN = 30;
function findSentenceCut(buf: string): number {
  // Prefer hard paragraph break first (the IliaLarchenko trick)
  const hardBreak = buf.indexOf('\n\n');
  if (hardBreak >= MIN_SENTENCE_LEN) return hardBreak + 2;
  // Otherwise scan for terminal punctuation followed by whitespace + capital
  // letter or newline. Scan from the end to grab the latest complete
  // sentence (lets us emit longer chunks per TTS call when buffer caught up).
  for (let i = buf.length - 2; i >= MIN_SENTENCE_LEN - 1; i--) {
    const c = buf[i];
    if (c !== '.' && c !== '!' && c !== '?') continue;
    const next = buf[i + 1] || '';
    const after = buf[i + 2] || '';
    // Need whitespace right after the punctuation
    if (!/\s/.test(next)) continue;
    // And a capital letter / digit / newline starts the next sentence
    // (or end of buffer — but in streaming case we'd just wait)
    if (after === '\n' || /[A-Z0-9"'(\[]/.test(after)) {
      return i + 1; // include punctuation, drop the space
    }
  }
  return -1;
}

// Default language: pick up the browser's UI language so users speaking
// e.g. Chinese on a zh-CN browser get zh-CN STT instead of mis-recognized
// English. Caller can still override via opts.lang.
function defaultLang(): string {
  if (typeof navigator === 'undefined') return 'en-US';
  return navigator.language || 'en-US';
}

export function useVoiceLoop(opts: UseVoiceLoopOptions): VoiceLoopApi {
  const { enabled, lang = defaultLang(), onFinalUtterance, onBargeIn } = opts;
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);

  // wantListeningRef tracks the user's INTENT (mic toggle on?). We can't rely
  // on the `listening` React state inside r.onend because that closure would
  // capture stale values: when the user clicks Start, `listening` is false at
  // closure-creation time, then setListening(true) flips it but the closure
  // still sees false. Browser auto-stops SR every ~60s and fires onend, and
  // without this ref we'd never restart — surface symptom is "voice records
  // for a minute then silently dies".
  const wantListeningRef = useRef(false);

  // Buffer of bot tokens not yet spoken. We slice completed sentences off
  // the front and feed them to speechSynthesis.
  const speakBufferRef = useRef('');
  // Once we hit the #NOTES# delimiter, stop routing tokens to TTS — they
  // become silent context for grading.
  const notesEnteredRef = useRef(false);
  // Latest onBargeIn / onFinalUtterance refs so SR handlers don't capture
  // stale closures
  const onFinalRef = useRef(onFinalUtterance);
  const onBargeRef = useRef(onBargeIn);
  useEffect(() => { onFinalRef.current = onFinalUtterance; }, [onFinalUtterance]);
  useEffect(() => { onBargeRef.current = onBargeIn; }, [onBargeIn]);

  const caps: VoiceLoopCaps = {
    canRecognize: typeof window !== 'undefined' && getRecognitionCtor() != null,
    canSpeak: typeof window !== 'undefined' && 'speechSynthesis' in window,
  };

  // ── speak ─────────────────────────────────────────────────────────────
  const stopSpeaking = useCallback(() => {
    if (caps.canSpeak) window.speechSynthesis.cancel();
    speakBufferRef.current = '';
    notesEnteredRef.current = false;
    setSpeaking(false);
  }, [caps.canSpeak]);

  const speakStreamReset = useCallback(() => {
    stopSpeaking();
  }, [stopSpeaking]);

  const speakSentence = useCallback((sentence: string) => {
    if (!caps.canSpeak) return;
    const trimmed = sentence.trim();
    if (!trimmed) return;
    const utter = new SpeechSynthesisUtterance(trimmed);
    utter.lang = lang;
    utter.rate = 1.0;
    utter.pitch = 1.0;
    utter.onstart = () => setSpeaking(true);
    utter.onend = () => {
      // Only flip back to false when the queue is fully drained
      if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
        setSpeaking(false);
      }
    };
    window.speechSynthesis.speak(utter);
  }, [caps.canSpeak, lang]);

  const drainBuffer = useCallback(() => {
    let buf = speakBufferRef.current;
    while (true) {
      const cut = findSentenceCut(buf);
      if (cut < 0) break;
      const sentence = buf.slice(0, cut);
      buf = buf.slice(cut).replace(/^\s+/, '');
      speakSentence(sentence);
    }
    speakBufferRef.current = buf;
  }, [speakSentence]);

  const speakStreamPush = useCallback((chunk: string) => {
    if (!caps.canSpeak) return;
    if (!chunk) return;
    if (notesEnteredRef.current) return; // silent channel after #NOTES#
    // Split on #NOTES# delimiter — flush pre-notes text, drop post-notes
    const notesIdx = chunk.indexOf('#NOTES#');
    if (notesIdx >= 0) {
      const before = chunk.slice(0, notesIdx);
      if (before) {
        speakBufferRef.current += before;
        drainBuffer();
      }
      // Flush whatever's left in buffer that doesn't have a sentence cut
      // — we want the user to hear the tail before going silent
      const tail = speakBufferRef.current.trim();
      if (tail.length > 0) {
        speakSentence(tail);
        speakBufferRef.current = '';
      }
      notesEnteredRef.current = true;
      return;
    }
    speakBufferRef.current += chunk;
    drainBuffer();
  }, [caps.canSpeak, drainBuffer, speakSentence]);

  // Track the last interim snapshot at stop time, so if the browser fails
  // to fire a final after r.stop(), we can still rescue the user's words.
  const interimAtStopRef = useRef<string>('');
  const fallbackCommitTimerRef = useRef<number | null>(null);
  // Set by onresult right before calling onFinalRef — the fallback-commit
  // checks this to decide whether the browser already gave us a real final.
  const lastFinalAtRef = useRef<number>(0);

  // ── listen ────────────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    // Snapshot interim BEFORE r.stop() so we have a fallback if the browser
    // never fires a final on the current chunk (happens with some Chrome
    // builds when the user clicks Stop mid-word, or speaks quietly).
    const snapshot = interim.trim();
    interimAtStopRef.current = snapshot;
    const stoppedAt = Date.now();

    // wantListeningRef gates the auto-restart inside r.onend. Set to false
    // BEFORE calling r.stop() so the queued onend doesn't restart us.
    wantListeningRef.current = false;
    const r = recognitionRef.current;
    if (r) {
      // Use stop() not abort() — stop() flushes the current chunk as a final
      // result, abort() throws away in-flight audio. We want the user's last
      // sentence to land in their notes when they click Stop.
      try { r.stop(); } catch { /* noop */ }
    }
    setListening(false);
    // Don't clear interim immediately — leave it visible while we wait for
    // the fallback timer. The onend handler will clear it once SR really
    // shuts down. Visual will keep showing the last heard text.

    // Schedule a fallback commit: if no real final arrives within 1.5s and
    // the interim snapshot has content, push the snapshot as a final so it
    // doesn't vanish silently.
    if (fallbackCommitTimerRef.current) {
      clearTimeout(fallbackCommitTimerRef.current);
    }
    if (snapshot) {
      fallbackCommitTimerRef.current = window.setTimeout(() => {
        // Real final happened after stop? Don't double-commit.
        if (lastFinalAtRef.current > stoppedAt) return;
        const cb = onFinalRef.current;
        if (cb) cb(snapshot);
        // Clear visible interim now that we've rescued it
        setInterim('');
      }, 1500);
    }
  }, [interim]);

  // Expose a manual rescue: caller can grab the last interim text immediately
  // (e.g. user clicks "Use last heard text" button), in case both the real
  // final AND the fallback timer fail.
  const useLastHeard = useCallback(() => {
    const text = interimAtStopRef.current || interim.trim();
    if (!text) return false;
    const cb = onFinalRef.current;
    if (cb) cb(text);
    setInterim('');
    interimAtStopRef.current = '';
    return true;
  }, [interim]);

  const startListening = useCallback(() => {
    setError(null);
    if (!caps.canRecognize) {
      setError('Speech recognition is not available in this browser. Use Chrome or Edge.');
      return;
    }
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;
    // Tear down any prior instance — and clear the flag so its still-pending
    // onend won't try to restart the OLD instance after we wire up the new one
    wantListeningRef.current = false;
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* noop */ }
    }
    const r = new Ctor();
    r.lang = lang;
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (e) => {
      let interimText = '';
      let finalText = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        const transcript = result[0].transcript;
        if (result.isFinal) finalText += transcript;
        else interimText += transcript;
      }
      if (interimText) setInterim(interimText);
      if (finalText) {
        setInterim('');
        lastFinalAtRef.current = Date.now();
        const cb = onFinalRef.current;
        if (cb) cb(finalText.trim());
      }
    };
    r.onerror = (e) => {
      const err = e.error || 'unknown';
      // 'no-speech' / 'aborted' are normal idle states, not errors
      if (err === 'no-speech' || err === 'aborted') return;
      let msg: string;
      switch (err) {
        case 'not-allowed':
        case 'service-not-allowed':
          msg = 'Microphone permission denied. Click the lock icon in the URL bar to allow microphone access, then click Start again.';
          // Permission errors are terminal — don't auto-restart
          wantListeningRef.current = false;
          break;
        case 'audio-capture':
          msg = 'No microphone detected. Plug one in and try again.';
          wantListeningRef.current = false;
          break;
        case 'network':
          msg = 'Speech recognition needs internet (Chrome uses cloud STT). Check your connection.';
          break;
        case 'language-not-supported':
          msg = `Language "${lang}" not supported by this browser's STT.`;
          wantListeningRef.current = false;
          break;
        default:
          msg = `Voice recognition error: ${err}`;
      }
      setError(msg);
    };
    r.onspeechstart = () => {
      // Barge-in: if the bot is currently speaking, cut it off
      if (caps.canSpeak && (window.speechSynthesis.speaking || window.speechSynthesis.pending)) {
        window.speechSynthesis.cancel();
        setSpeaking(false);
        const cb = onBargeRef.current;
        if (cb) cb();
      }
    };
    r.onend = () => {
      // Browsers auto-stop after ~60s of silence even with continuous=true.
      // If the user still wants to listen (wantListeningRef === true), restart.
      // If the user clicked Stop (wantListeningRef === false), let it die.
      // Tiny setTimeout avoids synchronous re-entry into start().
      setTimeout(() => {
        if (recognitionRef.current !== r) return; // we got replaced
        if (!wantListeningRef.current) {
          // User explicitly stopped — make sure UI state matches
          setListening(false);
          setInterim('');
          recognitionRef.current = null;
          return;
        }
        try { r.start(); } catch (err) {
          // start() can throw "already started" if browser fires onend
          // spuriously; ignore. Other errors → surface.
          const msg = (err as Error).message || '';
          if (!/already/i.test(msg)) {
            setError(`Could not resume listening: ${msg}`);
            wantListeningRef.current = false;
            setListening(false);
          }
        }
      }, 50);
    };
    recognitionRef.current = r;
    try {
      wantListeningRef.current = true;
      r.start();
      setListening(true);
    } catch (e) {
      wantListeningRef.current = false;
      setError(`Could not start listening: ${(e as Error).message}`);
    }
  }, [caps.canRecognize, caps.canSpeak, lang]); // listening removed — handled via ref

  // Enabled-toggle: stop the loop when caller flips enabled→false.
  // CRITICAL: this MUST NOT abort the recognizer in cleanup — abort() throws
  // away the audio buffer without firing a final, which is exactly the bug
  // that made spoken answers vanish on Stop. We just call stopListening()
  // (which uses r.stop() to flush a final) and let the unmount-only effect
  // below handle true teardown.
  useEffect(() => {
    if (!enabled) {
      stopListening();
      stopSpeaking();
    }
  }, [enabled, stopListening, stopSpeaking]);

  // True unmount cleanup ([] deps) — only runs when the component using this
  // hook actually unmounts. Safe to abort here because there's no further UI
  // to deliver final transcripts to.
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch { /* noop */ }
      }
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    caps, listening, speaking, interim, error, lang,
    startListening, stopListening, useLastHeard,
    speakStreamReset, speakStreamPush, stopSpeaking,
  };
}
